// What a hub's events say, as pure functions over the Curator API's records.
// See Design > Exchange, "Events and demand". Test runs never count.

import type { EventRow, HubSummary, HubStats, Intent } from "./types.ts";

/** The hub's short name, the part of its name after the org, which Studio shows. Two hubs in different orgs can share one, so nothing is found by it: routes and requests use the Hub ID. */
export const shortName = (hub: string) => hub.slice(hub.indexOf("/") + 1);
/** A hub's name as a list of the caller's hubs shows it: its short name, or its full name when another of them has the same short name. */
export const pickerName = (hub: string, all: readonly string[]) =>
  all.some((other) => other !== hub && shortName(other) === shortName(hub)) ? hub : shortName(hub);

/** Outcomes for asks the Exchange chose not to send to the hub. */
export const NOT_DISPATCHED = ["NotTargeted", "BelowMatchCutoff", "BelowDispatchThreshold", "Demonetized", "RankedBelowMaxAnswers", "TotalCostCap", "NoHubSecret"];

export type OutcomeKey = "answered" | "detail" | "declined" | "planned" | "pricedOut" | "notFound" | "error";
export const OUTCOME_KEYS: OutcomeKey[] = ["answered", "detail", "declined", "planned", "pricedOut", "notFound", "error"];

// Outcomes are open strings from the Exchange, so they're looked up rather than matched: any other
// outcome the hub returned is an error, and a not-dispatched one isn't an ask the hub received.
const KEY_BY_OUTCOME = new Map<string, OutcomeKey | null>([
  ["Answer", "answered"], ["MissingParameters", "detail"], ["OutOfScope", "declined"], ["NotImplemented", "planned"],
  ["PriceTooLow", "pricedOut"], ["NotFound", "notFound"], ["DirectFetch", null],
]);
const NOT_DISPATCHED_FIELD = new Map<string, "belowThreshold" | "outranked" | "belowCutoff">([
  ["BelowDispatchThreshold", "belowThreshold"], ["RankedBelowMaxAnswers", "outranked"], ["BelowMatchCutoff", "belowCutoff"],
]);

/** The group a curator reads an outcome in, or null for one that isn't an ask the hub received. */
export function outcomeKey(outcome: string): OutcomeKey | null {
  const known = KEY_BY_OUTCOME.get(outcome);
  if (known !== undefined) return known;
  return NOT_DISPATCHED.includes(outcome) ? null : "error";
}

const zero = (): Record<OutcomeKey, number> => ({ answered: 0, detail: 0, declined: 0, planned: 0, pricedOut: 0, notFound: 0, error: 0 });

export type HubOutcomes = {
  received: number;
  byKey: Record<OutcomeKey, number>;
  notDispatched: { belowThreshold: number; outranked: number; belowCutoff: number; other: number; total: number };
};

/** A hub's all-time outcome counts, grouped. */
export function hubOutcomes(outcomes: Record<string, number>): HubOutcomes {
  const byKey = zero();
  const nd = { belowThreshold: 0, outranked: 0, belowCutoff: 0, other: 0, total: 0 };
  for (const [outcome, n] of Object.entries(outcomes)) {
    const key = outcomeKey(outcome);
    if (key) { byKey[key] += n; continue; }
    if (!NOT_DISPATCHED.includes(outcome)) continue;
    nd.total += n;
    nd[NOT_DISPATCHED_FIELD.get(outcome) ?? "other"] += n;
  }
  return { received: OUTCOME_KEYS.reduce((sum, k) => sum + byKey[k], 0), byKey, notDispatched: nd };
}

export type IntentActivity = {
  intent: string; asked: number; answered: number; revenueUsd: number;
  byKey: Record<OutcomeKey, number>; pricedOutBidsPerMillion: number[]; answerMs: number[]; events: EventRow[];
};

/** Asks the hub received, per intent, from a page of events (newest first). */
export function activityByIntent(events: ReadonlyArray<EventRow>): Map<string, IntentActivity> {
  const out = new Map<string, IntentActivity>();
  for (const e of events) {
    if (e.test_run || !e.intent) continue;
    const key = outcomeKey(e.outcome);
    if (!key) continue;
    let a = out.get(e.intent);
    if (!a) { a = { intent: e.intent, asked: 0, answered: 0, revenueUsd: 0, byKey: zero(), pricedOutBidsPerMillion: [], answerMs: [], events: [] }; out.set(e.intent, a); }
    a.asked += 1;
    a.byKey[key] += 1;
    a.events.push(e);
    if (key === "answered") {
      a.answered += 1;
      const price = Number(e.detail?.price);
      if (Number.isFinite(price)) a.revenueUsd += price / 1e6;
      if (e.ms !== null) a.answerMs.push(e.ms);
    }
    if (key === "pricedOut" && e.bid_nano !== null) a.pricedOutBidsPerMillion.push(e.bid_nano / 1000);
  }
  return out;
}

/** Nearest-rank percentile; null for no values. */
export function percentile(values: ReadonlyArray<number>, p: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1))] ?? null;
}

export const isLive = (intent: Intent) => Boolean(intent.tool);

export type AttentionKey = "creditsBack" | "declined" | "pricedOut" | "planned" | "notFound" | "error";
export type Attention = { key: AttentionKey; count: number; of?: number; offeredUsd?: number; intent?: string };

/** What needs the curator's attention, most costly first. An intent names the question with the most such asks. */
export function attention(stats: HubStats, activity: Map<string, IntentActivity>, declined?: { questions: number; bidUsd: number }): Attention[] {
  const { byKey } = hubOutcomes(stats.outcomes);
  const top = (key: OutcomeKey) => {
    const intent = [...activity.values()].filter((a) => a.byKey[key] > 0).sort((a, b) => b.byKey[key] - a.byKey[key])[0]?.intent;
    return intent !== undefined ? { intent } : {};
  };
  const items: Attention[] = [
    { key: "creditsBack", count: stats.refunds, of: stats.answers },
    declined ? { key: "declined", count: declined.questions, offeredUsd: declined.bidUsd } : { key: "declined", count: byKey.declined },
    { key: "pricedOut", count: byKey.pricedOut, ...top("pricedOut") },
    { key: "planned", count: byKey.planned, ...top("planned") },
    { key: "notFound", count: byKey.notFound, ...top("notFound") },
    { key: "error", count: byKey.error, ...top("error") },
  ];
  return items.filter((i) => i.count > 0);
}

/** The hub's questions, live first, each with its activity. */
export function questionRows(hub: HubSummary, activity: Map<string, IntentActivity>) {
  return [...hub.manifest.intents]
    .map((intent) => ({ intent, live: isLive(intent), activity: activity.get(intent.id) ?? null }))
    .sort((a, b) => Number(b.live) - Number(a.live) || (b.activity?.revenueUsd ?? 0) - (a.activity?.revenueUsd ?? 0) || (b.activity?.asked ?? 0) - (a.activity?.asked ?? 0));
}
